import { haversine } from "./geo";

export type GpxPoint = [lon: number, lat: number, ele: number | null];
export type GpxWaypoint = { name: string; lat: number; lon: number; ele: number | null };
export type GpxData = {
  tracks: GpxPoint[][];
  waypoints: GpxWaypoint[];
  distanceKm: number;
  gainM: number;
  bounds: [number, number, number, number] | null;
};

const num = (v: string | null | undefined) => {
  const n = v == null ? NaN : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Regex-based GPX reader (works in the page, the server and Node — no DOMParser needed).
 * Reads trk/trkseg/trkpt and rte/rtept as tracks, wpt as waypoints.
 */
export function parseGpx(xml: string): GpxData {
  const tracks: GpxPoint[][] = [];
  const segRe = /<(trkseg|rte)\b[^>]*>([\s\S]*?)<\/\1>/gi;
  const ptRe = /<(trkpt|rtept)\b([^>]*?)(?:\/>|>([\s\S]*?)<\/\1>)/gi;
  const attr = (s: string, name: string) => s.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']+)["']`, "i"))?.[1];
  const tag = (s: string | undefined, name: string) => s?.match(new RegExp(`<${name}\\b[^>]*>([\\s\\S]*?)</${name}>`, "i"))?.[1];
  let m: RegExpExecArray | null;
  while ((m = segRe.exec(xml))) {
    const pts: GpxPoint[] = [];
    let p: RegExpExecArray | null;
    ptRe.lastIndex = 0;
    while ((p = ptRe.exec(m[2]!))) {
      const lat = num(attr(p[2]!, "lat"));
      const lon = num(attr(p[2]!, "lon"));
      if (lat === null || lon === null) continue;
      pts.push([lon, lat, num(tag(p[3], "ele"))]);
    }
    if (pts.length > 1) tracks.push(pts);
  }
  const waypoints: GpxWaypoint[] = [];
  const wptRe = /<wpt\b([^>]*?)(?:\/>|>([\s\S]*?)<\/wpt>)/gi;
  while ((m = wptRe.exec(xml))) {
    const lat = num(attr(m[1]!, "lat"));
    const lon = num(attr(m[1]!, "lon"));
    if (lat === null || lon === null) continue;
    const name = decodeXml(tag(m[2], "name")?.trim() ?? "");
    waypoints.push({ name, lat, lon, ele: num(tag(m[2], "ele")) });
  }
  let dist = 0;
  let gain = 0;
  let w = Infinity,
    s = Infinity,
    e = -Infinity,
    n = -Infinity;
  for (const t of tracks) {
    let lastEle: number | null = null;
    for (let i = 0; i < t.length; i++) {
      const [lon, lat, ele] = t[i]!;
      w = Math.min(w, lon);
      e = Math.max(e, lon);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
      if (i > 0) dist += haversine(t[i - 1]![1], t[i - 1]![0], lat, lon);
      // 3 m hysteresis to ignore GPS altitude noise
      if (ele !== null) {
        if (lastEle === null) lastEle = ele;
        else if (ele - lastEle > 3) {
          gain += ele - lastEle;
          lastEle = ele;
        } else if (lastEle - ele > 3) lastEle = ele;
      }
    }
  }
  for (const wp of waypoints) {
    w = Math.min(w, wp.lon);
    e = Math.max(e, wp.lon);
    s = Math.min(s, wp.lat);
    n = Math.max(n, wp.lat);
  }
  return {
    tracks,
    waypoints,
    distanceKm: Math.round(dist / 10) / 100,
    gainM: Math.round(gain),
    bounds: Number.isFinite(w) ? [w, s, e, n] : null,
  };
}

function decodeXml(s: string) {
  return s
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}
